import type { CurrentUser } from '@/auth/permissions'
import { visibleItems, visibleTopItems } from './navigation'
import { translate, type Locale } from '@/i18n'

/**
 * Палитра команд (ТЗ §5.1).
 *
 * Список команд строится из той же навигации, что и меню, и фильтруется теми же правами.
 * Отдельный список привёл бы к палитре, открывающей раздел, которого в меню нет, — и это
 * обнаружилось бы не как несоответствие, а как 403 на пустом экране.
 */

export interface Command {
  id: string
  /** Уже переведённая подпись: палитра сравнивает ввод с тем, что видит пользователь. */
  label: string
  /** Группа для заголовка в списке. */
  groupKey: string
  path: string
}

/** Доступные пользователю команды. */
export function commandsFor(user: CurrentUser | null, locale: Locale): Command[] {
  return [...visibleTopItems(user), ...visibleItems(user)].map((item) => ({
    id: item.path,
    label: translate(item.labelKey, locale),
    groupKey: 'palette.sections',
    path: item.path,
  }))
}

/**
 * Отбирает команды по вводу.
 *
 * Подстрочное совпадение без учёта регистра, а не нечёткий поиск: список из двадцати
 * пунктов не нуждается в fuzzy, а нечёткое совпадение на коротком запросе показывает
 * почти всё и перестаёт помогать.
 *
 * Совпадения в начале подписи идут первыми: набирая «сд», человек ищет «Сделки», а не
 * «Коммерческие предложения», где эти буквы встретились в середине.
 */
export function filterCommands(commands: Command[], query: string): Command[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return commands

  const matches = commands.filter((command) => command.label.toLowerCase().includes(needle))

  return matches.sort((a, b) => {
    const aStarts = a.label.toLowerCase().startsWith(needle)
    const bStarts = b.label.toLowerCase().startsWith(needle)
    if (aStarts !== bStarts) return aStarts ? -1 : 1
    return a.label.localeCompare(b.label)
  })
}

/**
 * Горячие клавиши приложения.
 *
 * Ctrl+K для палитры — соглашение, которое пользователь уже знает по другим системам;
 * своя комбинация здесь была бы оригинальностью за счёт человека.
 */
export interface Shortcut {
  key: string
  ctrl?: boolean
  shift?: boolean
  description: string
}

export const SHORTCUTS: Record<string, Shortcut> = {
  palette: { key: 'k', ctrl: true, description: 'palette.hint' },
  search: { key: '/', description: 'search.placeholder' },
}

/**
 * Совпадает ли событие с комбинацией.
 *
 * Проверяется и metaKey: на macOS сочетание набирают через Cmd, и требовать Ctrl значит
 * сделать палитру недоступной половине пользователей.
 */
export function matchesShortcut(event: KeyboardEvent, shortcut: Shortcut): boolean {
  if (event.key.toLowerCase() !== shortcut.key) return false
  if (shortcut.ctrl && !(event.ctrlKey || event.metaKey)) return false
  if (!shortcut.ctrl && (event.ctrlKey || event.metaKey)) return false
  if (shortcut.shift && !event.shiftKey) return false
  return true
}

/**
 * Нужно ли игнорировать горячую клавишу.
 *
 * В поле ввода «/» — это символ, а не команда. Без этой проверки пользователь не сможет
 * написать дробь в комментарии: фокус уедет в поиск на первом же нажатии.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  // Приведение обязательно: `isContentEditable` отсутствует у элементов вне документа
  // и в jsdom, и вернуть `undefined` отсюда значит отдать вызывающему значение,
  // которое ведёт себя как false, но не равно ему.
  return target.isContentEditable === true
}
